// buildAiHomePath.js — Home hub deep links for Ai sessions

import { isAiChannelKeyForUser } from 'spaces/utils/aiChannelKeys';

function cleanString(value) {
  return String(value || '').trim();
}

/**
 * Resolves the active Ai session key from a Home `?session=` link or `/channels/` route.
 *
 * @param {object} location - Router location.
 * @param {string} baseKey - Active base key.
 * @param {string} userKey - Current user key.
 * @returns {string}
 */
export function getAiSessionKeyFromLocation(location = {}, baseKey = '', userKey = '') {
  const search = String(location?.search || '');
  const sessionKey = cleanString(new URLSearchParams(search).get('session'));
  if (isAiChannelKeyForUser(sessionKey, baseKey, userKey)) return sessionKey;

  const pathname = String(location?.pathname || '');
  if (!pathname.startsWith('/channels/')) return '';

  let routeKey = '';
  try {
    routeKey = decodeURIComponent(pathname.split('/')[2] || '');
  } catch {
    routeKey = pathname.split('/')[2] || '';
  }

  return isAiChannelKeyForUser(routeKey, baseKey, userKey) ? routeKey : '';
}

/**
 * Build `/?session=…` Home hub path for an Ai session.
 */
export function buildAiHomePath({
  channelKey,
  baseKey,
  messageKey = '',
  threadRootKey = '',
} = {}) {
  const cleanChannelKey = cleanString(channelKey);
  if (!cleanChannelKey) return '/';

  const params = new URLSearchParams();
  params.set('session', cleanChannelKey);

  const cleanBaseKey = cleanString(baseKey);
  const cleanMessageKey = cleanString(messageKey);
  const cleanThreadRootKey = cleanString(threadRootKey);

  if (cleanBaseKey) params.set('baseKey', cleanBaseKey);
  if (cleanThreadRootKey) params.set('threadRootKey', cleanThreadRootKey);
  if (cleanMessageKey) params.set('messageKey', cleanMessageKey);

  return `/?${params.toString()}`;
}
